import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Reveal } from "./Reveal";
import { ArchWindow } from "./ArchWindow";

const TIERS = [
  { key: "25", label: "$25", impact: "Keeps a church website online for three months" },
  { key: "50", label: "$50", impact: "Launches a local outreach campaign for one congregation" },
  { key: "100", label: "$100", impact: "Builds a full sermon streaming page" },
  { key: "250", label: "$250", impact: "Funds a complete church website, start to finish" },
];

export const DonateSection = () => {
  const [tier, setTier] = useState("50");
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one_time");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({ total_raised: 0, donation_count: 0, goal: 50000 });

  useEffect(() => {
    api.get("/donations/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const donate = async () => {
    setLoading(true);
    try {
      const body = { origin_url: window.location.origin, frequency };
      if (custom) body.custom_amount = parseFloat(custom);
      else body.tier = tier;
      const { data } = await api.post("/payments/checkout", body);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const pct = Math.min(100, (summary.total_raised / summary.goal) * 100);
  const selected = TIERS.find((t) => t.key === tier);

  return (
    <section id="donate" className="py-28 md:py-36 bg-[#FFF5F2] relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(232,76,76,0.08) 0%, transparent 70%)" }} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16 items-start relative">
        <Reveal>
          <div className="flex items-center gap-3">
            <ArchWindow size={14} bar="#FFF5F2" />
            <span className="font-orbitron text-xs tracking-[0.4em] text-[#E84C4C]">PARTNER WITH ME</span>
          </div>
          <h2 className="font-orbitron font-bold text-2xl sm:text-3xl lg:text-4xl text-[#120716] mt-4 leading-tight">
            I CAN'T BUILD<br />THIS ALONE.
          </h2>
          <p className="mt-4 italic font-light text-[#C13B3B] text-sm md:text-base">"Give, and it will be given to you." — Luke 6:38</p>
          <p className="mt-6 text-[#120716]/70 leading-relaxed text-sm md:text-base max-w-md">
            Every website I build is free to the church that receives it, but the hosting, the tools,
            and the hours are not free. Donors carry that cost so pastors never have to. When you give
            here, you're not funding overhead; you're putting a specific church online that could never
            have afforded to be there, in the name of the One who told us to go.
          </p>
          <div className="mt-10 glass rounded-2xl p-6" data-testid="donation-progress">
            <div className="flex justify-between items-end mb-3">
              <div>
                <div className="font-orbitron font-bold text-2xl text-[#120716]">${summary.total_raised.toLocaleString()}</div>
                <div className="text-xs tracking-widest uppercase text-[#120716]/50 mt-1">raised of ${summary.goal.toLocaleString()} annual goal</div>
              </div>
              <div className="text-right">
                <div className="font-orbitron font-bold text-2xl text-[#E84C4C]">{summary.donation_count}</div>
                <div className="text-xs tracking-widest uppercase text-[#120716]/50 mt-1">gifts</div>
              </div>
            </div>
            <div className="h-2 rounded-full bg-[#120716]/10 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.max(2, pct)}%`, background: "linear-gradient(90deg, #E84C4C, #F3A0A0)" }} />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="glass rounded-2xl p-8 md:p-10" data-testid="donation-card">
            <div className="flex rounded-full bg-[#120716]/5 p-1 mb-8">
              {[["one_time", "One-Time"], ["monthly", "Monthly"]].map(([k, l]) => (
                <button
                  key={k}
                  data-testid={`donate-frequency-${k}`}
                  onClick={() => setFrequency(k)}
                  className={`flex-1 rounded-full py-2.5 text-sm font-semibold transition-all duration-300 ${
                    frequency === k ? "bg-[#E84C4C] text-white" : "text-[#120716]/60 hover:text-[#120716]"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {TIERS.map((t) => (
                <button
                  key={t.key}
                  data-testid={`donate-tier-${t.key}`}
                  onClick={() => { setTier(t.key); setCustom(""); }}
                  className={`rounded-xl border py-5 font-orbitron font-bold text-xl transition-all duration-300 ${
                    tier === t.key && !custom
                      ? "border-[#E84C4C] bg-[#E84C4C]/10 text-[#E84C4C] shadow-[0_0_24px_rgba(232,76,76,0.2)]"
                      : "border-[#120716]/15 text-[#120716]/60 hover:border-[#E84C4C]/50"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <input
              data-testid="donate-custom-amount"
              type="number"
              min="1"
              placeholder="Or enter a custom amount ($)"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              className="mt-4 w-full rounded-xl bg-white border border-[#120716]/15 px-4 py-4 text-sm text-[#120716] placeholder:text-[#120716]/35 focus:outline-none focus:border-[#E84C4C] transition-colors"
            />
            <p className="mt-4 text-xs text-[#C13B3B]/80 italic min-h-[16px]">
              {custom ? "Whatever you give, give it cheerfully. — 2 Corinthians 9:7" : selected?.impact}
            </p>
            {custom && frequency === "monthly" && (
              <p className="mt-1 text-xs text-[#E84C4C]">Custom amounts are one-time gifts.</p>
            )}
            <button
              onClick={donate}
              disabled={loading}
              data-testid="donate-checkout-btn"
              className="mt-6 w-full rounded-full bg-[#E84C4C] hover:bg-[#f05e5e] disabled:opacity-50 text-white font-semibold py-4 transition-all duration-300 hover:shadow-[0_0_32px_rgba(232,76,76,0.5)]"
            >
              {loading ? "Opening secure checkout..." : `Give ${custom ? `$${custom}` : selected?.label}${frequency === "monthly" && !custom ? " / month" : ""}`}
            </button>
            <p className="mt-4 text-center text-[10px] tracking-widest uppercase text-[#120716]/40">
              Secure payment via Stripe · Tax-deductible
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
